import React, { useState } from 'react';
import type { FC } from 'react';
import { isType, match } from 'variant';
import { Spinner, useAsync } from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useErrorReporter } from '@wix/cli-error-reporting';
import { useTranslation } from '@wix/cli-i18n';
import type { RepoType } from '@wix/package-manager';
import type { CommandOptions } from '../../parse-command-options.js';
import {
  AppNameOptions,
  parseCommandOptions,
} from '../../parse-command-options.js';
import { DeveloperAppsStatus } from '../../types.js';
import type { GenerationData, CreationFlow } from '../../types.js';
import { CreateAppOrExtendExisting } from '../CreateAppOrExtendExisting.js';
import { NewAppFlow } from './NewAppFlow/RegularFlow.js';
import { NewAppAutoFlow } from './NewAppFlow/AutoFlow.js';

import { ExistingAppFlow } from './ExistingAppFlow.js';
import { StartFromTemplateMessage } from '../StartFromTemplateMessage.js';

interface QuestionsProps {
  commandOptions: CommandOptions;
  targetParentFolder: string;
  repoType: RepoType;
  onSubmit: (data: GenerationData) => void;
}

export const Questions: FC<QuestionsProps> = ({
  commandOptions,
  targetParentFolder,
  repoType,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const devCenterClient = useDevCenterClient();
  const { reportError } = useErrorReporter();
  const { getDeveloperApps } = useDevCenterClient();

  const [appCreationFlow, setAppCreationFlow] = useState<CreationFlow>();

  const { status } = useAsync(async () => {
    const { template, appName } = await parseCommandOptions(
      targetParentFolder,
      commandOptions,
      {
        devCenterClient,
      }
    );

    if (template) {
      return {
        template,
        appName,
      };
    }

    try {
      const developerApps = await getDeveloperApps();

      return {
        developerApps,
        template,
        developerAppsStatus: developerApps.length
          ? DeveloperAppsStatus.Available()
          : DeveloperAppsStatus.NoApps(),
      };
    } catch (error) {
      reportError(error);
      return {
        developerApps: [],
        template,
        developerAppsStatus: DeveloperAppsStatus.FailedLoadingApps(),
      };
    }
  }, []);

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('general.loading')} />,
    Success: ({
      result: { template, appName, developerApps, developerAppsStatus },
    }) => {
      if (template) {
        return (
          <>
            <StartFromTemplateMessage template={template} />

            {isType(appName, AppNameOptions.Provided) ? (
              <NewAppAutoFlow
                extendAppId={commandOptions.extendAppId}
                appName={appName}
                onSubmit={onSubmit}
                template={template}
              />
            ) : (
              <NewAppFlow
                onSubmit={onSubmit}
                extendAppId={commandOptions.extendAppId}
                template={template}
                targetParentFolder={targetParentFolder}
                repoType={repoType}
              />
            )}
          </>
        );
      }

      if (isType(developerAppsStatus, 'NoApps')) {
        return (
          <NewAppFlow
            onSubmit={onSubmit}
            targetParentFolder={targetParentFolder}
            repoType={repoType}
          />
        );
      }

      return (
        <>
          <CreateAppOrExtendExisting
            developerAppsStatus={developerAppsStatus}
            onStartCreationFlow={setAppCreationFlow}
          />

          {appCreationFlow === 'new' && (
            <NewAppFlow
              onSubmit={onSubmit}
              targetParentFolder={targetParentFolder}
              repoType={repoType}
            />
          )}

          {appCreationFlow === 'existing' && (
            <ExistingAppFlow
              developerApps={developerApps}
              onSubmit={onSubmit}
              targetParentFolder={targetParentFolder}
              repoType={repoType}
            />
          )}
        </>
      );
    },
  });
};
